import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import cx from 'classnames';
import _ from 'lodash';
import AppActions from '../../actions/AppActions';
import ContentStore from '../../stores/InternetBankingContentStore';
import DataStore from '../../stores/InternetBankingDataStore';
import AppStore from '../../stores/AppStore';
import SessionStore from '../../stores/SessionStore';
import ValidationStore from '../../stores/ValidationStore';
import SiteHeaderStore from '../../stores/SiteHeaderStore';
import InternetBankingActions from '../../actions/InternetBankingActions';
import SessionActionCreator from '../../actions/SessionActionCreator';
import SiteHeader from '../../includes/SiteHeader';
import SiteHeaderUtils from '../../utils/SiteHeaderUtils';
import BrowserUtils from '../../utils/BrowserUtils';
import NonBCustomerError from '../common/NonBCustomerError';
import SvgIcon from '../common/SvgIcon';
import { focusOnFirstComponent } from '../common/focusOnFirstComponent';
import SessionApiUtils from '../../utils/SessionApiUtils';
import AccessibilityMessage from '../common/AccessibilityMessage';
import { isB, isVM, isYB, isCB } from '../../utils/BrandUtils';
import {
  CHATBOT_WIDGET_IFRAME_WINDOW_ID,
  CHATBOT_LETS_CHAT_LINK_ID,
} from '../../constants/ChatbotConstants';
import UserManagementStore from '../../stores/UserManagementStore';

function getStateFromStores() {
  return {
    content: ContentStore.getAll(),
    data: DataStore.getAll(),
    session: SessionStore.getAll(),
    validations: ValidationStore.getAll(),
    sessionTimeoutFlag: SessionStore.getSessionTimeout(),
    webTask: AppStore.getAll().webTaskId,
    hideHeaderFlag: SiteHeaderStore.getHideHeaderFlag(),
  };
}

class InternetBanking extends Component {
  constructor(props) {
    super(props);

    this.onWindowBeforeUnload = this.onWindowBeforeUnload.bind(this);
    this.shouldShowHeader = this.shouldShowHeader.bind(this);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.headerStoreChange = this.headerStoreChange.bind(this);

    this.state = getStateFromStores();
  }

  componentDidMount() {
    document.body.classList.add(
      cx({
        dyb: isB(),
        vm: isVM(),
        yb: isYB(),
        cb: isCB(),
      })
    );

    if (navigator.platform.search('Win') >= 0) {
      document.body.classList.add('os-win');
    } else if (navigator.platform.search('Mac') >= 0) {
      document.body.classList.add('os-mac');
    }

    ContentStore.addChangeListener(this.onStoreChange);
    DataStore.addChangeListener(this.onStoreChange);
    SessionStore.addChangeListener(this.onStoreChange);
    ValidationStore.addChangeListener(this.onStoreChange);
    SiteHeaderStore.addChangeListener(this.headerStoreChange);
    AppStore.addChangeListener(this.onStoreChange);
    focusOnFirstComponent(this.refs.pageTitle);
    window.addEventListener('beforeunload', this.onWindowBeforeUnload);
    window.addEventListener('unload', this.onWindowBeforeUnload);
  }

  componentWillUnmount() {
    ContentStore.removeChangeListener(this.onStoreChange);
    DataStore.removeChangeListener(this.onStoreChange);
    SessionStore.removeChangeListener(this.onStoreChange);
    ValidationStore.removeChangeListener(this.onStoreChange);
    AppStore.removeChangeListener(this.onStoreChange);
    SiteHeaderStore.removeChangeListener(this.headerStoreChange);
  }

  /**
   * Triggered before a refresh or tab close.
   *
   * @param  {Event} e
   */
  onWindowBeforeUnload() {
    if (SessionStore.isTokenDelete() && process.env.NODE_ENV === 'production') {
      !_.isEmpty(this.state.session.accessToken) &&
        SessionApiUtils.deleteAccessToken(this.state.session.accessToken);
      localStorage.clear();
      SessionStore.setTokenDelete(false);
    }
  }

  shouldShowHeader() {
    return (
      (!_.includes(SiteHeaderUtils.getAdaptiveModules(), this.state.webTask) ||
        BrowserUtils.getView() === 'desktop') &&
      !this.state.hideHeaderFlag
    );
  }

  /**
   * Store has been updated.
   *
   * @param  {Event} e
   */
  onStoreChange() {
    this.setState(getStateFromStores());
  }

  closePopup() {
    this.setState({ sessionTimeoutFlag: false });
    SessionActionCreator.resetSessionTimeOutFlag();
    InternetBankingActions.resetUsername();
    AppActions.logoutUser();
    InternetBankingActions.navigateToWebTask('WEB-AUTHENTICATION');
  }

  headerStoreChange() {
    if (this.state.hideHeaderFlag !== SiteHeaderStore.getHideHeaderFlag()) {
      this.setState({ hideHeaderFlag: SiteHeaderStore.getHideHeaderFlag() });
    }
  }

  render() {
    const { children, ...props } = this.props;
    if (this.state.sessionTimeoutFlag) {
      // single spa app handles routing wihout context of this state.
      const elem = document.querySelector('#VM-manage-personal-details');
      elem.parentNode.removeChild(elem);
      // Remove let's chat on timeout
      try {
        if (UserManagementStore.isLiveChatMounted()) {
          document.getElementById(CHATBOT_WIDGET_IFRAME_WINDOW_ID).remove();
          document.getElementById(CHATBOT_LETS_CHAT_LINK_ID).remove();
          UserManagementStore.unmountLiveChat();
        }
      } catch (error) {}
    }

    return this.state.sessionTimeoutFlag ? (
      <NonBCustomerError
        headerContent={'Your session’s expired'}
        headerFlag={false}
        hideButton1
        page
        buttonName2={'Log back in'}
        bodyContent1={
          'For security reasons, your session has automatically expired. Tap the button below to log back in.'
        }
        bodyContent2={''}
        sessionTimeout={true}
        watchContent={
          <span>
            <SvgIcon id="icon-watch" width="32px" height="32px" />
          </span>
        }
      />
    ) : (
      <div>
        <Helmet
          title={this.state.content.landingPagePageHeader}
          titleTemplate={`%s | ${this.state.content.documentTitle}`}
        />
        <AccessibilityMessage />
        <div
          className={this.props.appData.requireOTPAuthentication ? 'blur' : ''}
        >
          {this.shouldShowHeader() && <SiteHeader />}
          {children &&
            React.cloneElement(children, {
              ...props,
              ...this.state,
            })}
        </div>
      </div>
    );
  }
}

InternetBanking.propTypes = {
  appData: PropTypes.object,
  location: PropTypes.object,
  children: PropTypes.node,
};

export default InternetBanking;
